/**
 * @fileoverview `EventSpineTemplate` (WEB-704) — the Event v2 shell every
 * module in this project mounts into.
 *
 * ## Why a sibling template rather than a refactor
 *
 * `EventDetailTemplate.tsx` was 1053 lines of one fixed composition. Reworking
 * it in place would have collided with every parallel module issue in M1/M2, so
 * this is a **sibling** template. WEB-726 swaps `/event/[slug]` onto this
 * shell via `EventSpineConnected`, which fills the slots and carries the
 * organisms the spine does not replace.
 *
 * ## Structure, outside-in
 *
 * ```
 * .event-spine                 ← WEB-702 size ladder (--evt-* custom props)
 *   .event-spine__bar--tablet  ← WEB-708: sticky top: var(--nav-stick-top), 768–1023 only
 *   .event-spine__column       ← centred, max 1216px, ladder inline padding
 *     <EventBreadcrumbs>       ← CLT › DISCOVER › <CATEGORY> › [GENRE] › <VENUE> › <EVENT>
 *     .event-spine__grid       ← grid-template-columns: var(--evt-grid)
 *       .event-spine__editorial  ← left track: title block, figure, modules
 *       .event-spine__panel      ← right track: position: var(--evt-panel-pos)
 *   .event-spine__bar--mobile  ← WEB-709: sticky bottom: 0, ≤767 only
 * ```
 *
 * The decision has one payload and three renderings — the decision section
 * inside the panel card at ≥1024, the tablet bar at 768–1023, the bottom bar
 * at ≤767. Exactly one of those three is in the accessibility tree at any
 * width; the other two are `display: none` via the band rules in
 * `eventSpine.css`. The panel card itself stays mounted below 1024 so the M3
 * satellites keep rendering (WEB-762). This file only decides WHERE each
 * mounts.
 *
 * The geometry lives entirely in `eventSpine.css`. There is **no JS viewport
 * read** — a media query resolving `--evt-grid` / `--evt-panel-pos` is what
 * turns two side-by-side tracks into one stacked column, so the server render
 * and the hydrated client render agree byte-for-byte with no measurement pass.
 *
 * ## What this template does NOT do
 *
 * It renders no module *body*. `resolveEventModules()` is called exactly once
 * per render and its ordered, self-gated result is mapped into slots; the
 * bodies arrive in M2 as renderers registered through {@link renderModule}.
 * Likewise the title block (WEB-705), the figure, and the decision panel
 * (WEB-706) are `ReactNode` slots — this file owns the spine, not its organs.
 *
 * Server component by default: no `'use client'`, no hooks. Only the pieces
 * that genuinely need interactivity (the panel CTA, the mobile bar) become
 * client islands, and they do so inside the slots their own issues own.
 */

import { isCityCode } from '@loci/city-editions';
import type { ReactElement, ReactNode } from 'react';

import { cn } from '@/components/atoms/primitives/utils';
import { EventBreadcrumbs } from '@/components/molecules/EventBreadcrumbs';
import { ShareButton } from '@/components/molecules/ShareButton';
import { EventSourceInfoBlocks } from '@/components/molecules/EventSourceInfoBlocks';
import { GENERIC_EVENT_LEXICON, getEventLexicon } from '@/config/eventLexicon';
import { resolveEventModules, type ResolvedEventModule } from '@/lib/eventDetail/modules';
import {
  readLocationVenueSlug,
  readResolvablePageSlug,
} from '@/lib/eventDetail/readResolvablePageSlug';
import type { EventDetailV2 } from '@/types/eventDetail';

/**
 * The M2 registry seam. A renderer receives one already-gated module and
 * returns its body — or `null` to leave the slot empty. `hasContent: false`
 * is handled in one place (WEB-719: `EventModuleSection` + the registry
 * short-circuit), so a renderer never has to implement the degrade note.
 */
export type EventModuleRenderer = (module: ResolvedEventModule) => ReactNode;

/**
 * The spine's full input contract. Everything is pure props — the route owns
 * fetching, and the four `ReactNode` slots plus {@link renderModule} are the
 * seams the M1/M2 issues fill without touching this file.
 */
export interface EventSpineTemplateProps {
  /** Event detail payload from `fetchEventDetail` (WEB-197, API-1404). */
  event: EventDetailV2;
  /**
   * The route's `/event/<pageSlug>` segment — the identifier the page's
   * `<link rel="canonical">` is built from. After the WEB-1088 308 it is the
   * event slug when one exists and the event id when it does not, so the Share
   * button shares exactly the canonical URL. Falls back to `event.slug` when
   * omitted (Storybook / isolated renders).
   */
  pageSlug?: string;
  /** City code rendered as the first breadcrumb segment (e.g. "CLT"). */
  cityCode: string;
  /** Full city name (e.g. "Charlotte") — accessible label for the city crumb. */
  cityLabel: string;
  /**
   * Category slug driving the lexicon: the breadcrumb category word, the
   * module order, and (downstream) every heading on the page. Anything outside
   * the canonical ten — including `null` — resolves to
   * `GENERIC_EVENT_LEXICON`, which is a normal outcome, not an error.
   */
  categorySlug?: string | null;
  /** WEB-705 — chips, category eyebrow, h1, standfirst. */
  titleBlock?: ReactNode;
  /** The editorial figure (aspect ratio rides `--evt-fig-ar`). */
  figure?: ReactNode;
  /** WEB-706 — the sticky decision panel. Visible at ≥1024 only. */
  panel?: ReactNode;
  /**
   * WEB-708 — the 768–1023 decision bar (`EventDecisionBar variant="bar"`).
   * Mounted above the content column so its `position: sticky;
   * top: var(--nav-stick-top)` pins it directly under the page chrome
   * (WEB-1111). Hidden at every other width by `eventSpine.css`, so passing
   * it is always safe.
   */
  tabletBar?: ReactNode;
  /**
   * WEB-709 — the ≤767 decision bar (`EventDecisionBar variant="bottom-bar"`).
   * Mounted at the very end of the spine so its `position: sticky; bottom: 0`
   * keeps it pinned to the bottom of the viewport for the spine's full length.
   * Hidden at every other width by `eventSpine.css`.
   */
  mobileBar?: ReactNode;
  /** M2 module-body registry; slots render empty without it. */
  renderModule?: EventModuleRenderer;
  /**
   * WEB-726 — carried organisms that belong in the editorial column after
   * M2 modules (social, nearby, trust footer). Lives inside
   * `.event-spine__editorial` so `--evt-sgap` applies.
   */
  afterEditorial?: ReactNode;
  /**
   * WEB-726 — carried organisms that belong after the 2-col grid
   * (RelatedShelf). Lives inside `.event-spine__column` so the 12-col
   * grid still ends at the panel, matching WEB-709.
   */
  afterGrid?: ReactNode;
  /** Optional additional class names on the `.event-spine` root. */
  className?: string;
}

/**
 * The enriched venue shape the event-detail view model carries. The generated
 * OpenAPI `Location` schema now includes `slug` (WEB-809); fixtures may still
 * set the pre-contract `venueSlug` extra field. Both are read through
 * {@link readLocationVenueSlug}. A structural cast is not `any`.
 */
interface SpineVenueLocation {
  name?: string | null;
}

/** Shown when the payload carries no venue name at all. */
const VENUE_FALLBACK_NAME = 'Venue TBA';

/**
 * Normalizes the venue crumb.
 *
 * WEB-328 / WEB-809: never fabricate a `/venue/venue` placeholder. A
 * whitespace-only, placeholder, or absent slug yields `null`, which tells
 * `EventBreadcrumbs` to render the venue as plain text rather than a link
 * that 404s.
 */
function resolveBreadcrumbVenue(event: EventDetailV2): { slug: string | null; name: string } {
  const location = (event.location ?? null) as SpineVenueLocation | null;
  const rawName = location?.name;
  return {
    slug: readLocationVenueSlug(event.location),
    name:
      typeof rawName === 'string' && rawName.trim().length > 0
        ? rawName.trim()
        : VENUE_FALLBACK_NAME,
  };
}

/**
 * Normalizes the WEB-830 genre crumb from `Event.sourceGenre` (PIP-234).
 *
 * Two rules, both presentational — neither invents or withholds data:
 *
 * 1. **Blank is absence.** `null`, `undefined`, and whitespace-only all yield
 *    `null`, which omits the crumb and leaves the trail in its pre-WEB-830
 *    five-segment shape. This is the common path: only the Ticketmaster lane
 *    populates `sourceGenre`, so Eventbrite and UNC-Charlotte events have
 *    none. Nothing is inferred to fill the gap, and no placeholder stands in
 *    for it — the honest-data contract PIP-234 established at ingest holds all
 *    the way to the DOM.
 *
 * 2. **Never repeat the category.** Ticketmaster's genre can equal its
 *    segment, and the lexicon's category words are the same nouns TM uses
 *    ("Music", "Sports", "Art"), so a faithful render can produce
 *    `… › MUSIC › MUSIC › THE FILLMORE › …`. Dropping an exact
 *    case-insensitive duplicate of the label already on screen removes a
 *    redundant crumb, not a distinction: the word is still rendered, once. A
 *    genre that merely overlaps ("Rock" under "Music") is kept.
 *
 * The value is otherwise passed through verbatim, in the source's own casing.
 * WEB-1189 C15: the crumb row is sentence case, so this function does not
 * uppercase the genre for display.
 */
function resolveBreadcrumbGenre(event: EventDetailV2, categoryLabel: string): string | null {
  const raw = event.sourceGenre;
  if (typeof raw !== 'string') {
    return null;
  }

  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return null;
  }

  return trimmed.toUpperCase() === categoryLabel.trim().toUpperCase() ? null : trimmed;
}

/**
 * EventSpineTemplate — pure-props presentational View. The route owns fetching;
 * this owns the two-track geometry, the breadcrumb trail, and the module slot
 * order. See the file header for the structural contract.
 */
export function EventSpineTemplate({
  event,
  pageSlug,
  cityCode,
  cityLabel,
  categorySlug,
  titleBlock,
  figure,
  panel,
  tabletBar,
  mobileBar,
  renderModule,
  afterEditorial,
  afterGrid,
  className,
}: EventSpineTemplateProps): ReactElement {
  // Trim once and reuse: the lexicon lookup and the crumb href must agree on
  // what the slug is. Resolving `'  music  '` to the generic lexicon while
  // still linking to `/category/music` would put two different categories on
  // the same page.
  const slug = typeof categorySlug === 'string' ? categorySlug.trim() : '';
  const lexicon = getEventLexicon(slug);
  const categoryLabel = lexicon.breadcrumbCategoryLabel;

  // Exactly once per render. Every downstream consumer reads this array —
  // nothing re-derives the order or re-checks the gating fields.
  const modules = resolveEventModules(event, lexicon);

  // WEB-422: a crumb only links when the slug names a category that actually
  // has a hub page. Non-empty is NOT enough — an unrecognised slug would link
  // to `/category/<garbage>` (a 404) while displaying the generic "EVENTS"
  // label, which is both a dead link and a label that contradicts its own href.
  // Anything unrecognised passes `null` instead, so the lexicon's word rides in
  // through `categoryFallbackLabel` as unlinked text.
  //
  // Identity comparison against the generic entry rather than a second
  // membership test: `getEventLexicon` returns exactly this object for every
  // slug outside the canonical ten, so reusing its verdict keeps "is this a
  // real category" stated in one place and unable to drift.
  const isCanonicalCategory = slug.length > 0 && lexicon !== GENERIC_EVENT_LEXICON;

  // Either way the **label comes from the lexicon**, never from the raw
  // category name on the wire — that is the point of WEB-703.
  const breadcrumbCategory = isCanonicalCategory ? { slug, label: categoryLabel } : null;
  const breadcrumbVenue = resolveBreadcrumbVenue(event);
  // Share the page's canonical identifier, not `event.slug` alone: most
  // Eventbrite and venue-site events carry no slug and live at `/event/<id>`,
  // which is still their canonical URL. Gating on `event.slug` hid the button
  // on every one of them.
  const shareSlug = readResolvablePageSlug(pageSlug) ?? readResolvablePageSlug(event.slug);
  // WEB-830. `categoryLabel` is the word actually rendered in the category
  // crumb on BOTH paths — the linked one above and the unlinked
  // `categoryFallbackLabel` below — so it is the right thing to de-duplicate
  // against regardless of which one this event takes.
  const breadcrumbGenre = resolveBreadcrumbGenre(event, categoryLabel);

  return (
    <div className={cn('event-spine', className)} data-testid="event-spine">
      {/* WEB-708 — first child of the spine on purpose. `.event-spine` is the
          bar's containing block, so mounting it here (above the column) is what
          lets `position: sticky; top: var(--nav-stick-top)` pin it under the
          page chrome instead of somewhere mid-document. Renders only in the
          768–1023 band. */}
      {tabletBar}

      <div className="event-spine__column">
        {/* WEB-605: the row wrapper owns the rule + padding so the border
            spans the full width under the trailing Share button. The button
            is a SIBLING of the nav (never inside it): crumbs, then Share. */}
        <div
          className="m-crumbrow w-full border-b border-loci-border py-3"
          data-testid="event-crumbrow"
        >
          <EventBreadcrumbs
            inRow
            cityCode={cityCode}
            cityLabel={cityLabel}
            category={breadcrumbCategory}
            categoryFallbackLabel={categoryLabel}
            genre={breadcrumbGenre}
            venue={breadcrumbVenue}
            eventTitle={event.title}
          />
          {/* No resolvable page identifier means no canonical URL to share:
              omit the button rather than share a fabricated one. */}
          {shareSlug != null ? (
            <ShareButton
              descriptor={{
                type: 'event',
                slug: shareSlug,
                title: event.title,
                ...(isCityCode(cityCode) ? { cityCode } : {}),
              }}
            />
          ) : null}
        </div>

        {/*
          Compatibility aliases for event-detail-links.spec.ts (WEB-726).
          Real boxes — not display:contents — so Playwright isVisible()
          still matches. event-detail-hero wraps the title so the spec's
          hero locator finds the spine title; event-detail-body-grid wraps
          only the 2-col grid so collectLinks does not double-count
          breadcrumbs or RelatedShelf.
        */}
        <div data-testid="event-detail-body-grid">
          <div className="event-spine__grid" data-testid="event-spine-grid">
            <div className="event-spine__editorial" data-testid="event-spine-editorial">
              {titleBlock ? <div data-testid="event-detail-hero">{titleBlock}</div> : null}
              {figure}
              <EventSourceInfoBlocks event={event} />
              {modules.map((module) => (
                <div
                  key={`${module.ordinal}-${module.kind}`}
                  className="event-spine__module"
                  data-testid={`event-spine-module-${module.kind}`}
                  data-module-kind={module.kind}
                  data-module-ordinal={module.ordinal}
                  data-has-content={module.hasContent ? 'true' : 'false'}
                >
                  {renderModule?.(module) ?? null}
                </div>
              ))}
              {afterEditorial}
            </div>

            <aside className="event-spine__panel" data-testid="event-spine-panel">
              {panel}
            </aside>
          </div>
        </div>
        {afterGrid}
      </div>

      {/* WEB-709 — last child of the spine on purpose. `position: sticky;
          bottom: 0` pins the bar to the bottom of the viewport for as long as
          its containing block (`.event-spine`) is on screen, and lets it come
          to rest at the spine's own end rather than overlapping whatever
          follows. Renders only at ≤767. */}
      {mobileBar}
    </div>
  );
}
